/* VULGAR:ERRORS — premium motion (GSAP + ScrollTrigger + Lenis).
   Serotoninn-grade: smooth momentum scroll, hero parallax, line-mask
   headline reveals, staggered card reveals. Built to the gsap-scrolltrigger
   / gsap-timeline / gsap-performance skill patterns.

   Safety: self-bootstraps GSAP + ScrollTrigger + Lenis from CDN; any failure
   degrades to a fully functional static site. Hidden states are set ONLY
   after GSAP confirms loaded. Gated behind prefers-reduced-motion. */
(function () {
  var GSAP_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.7/dist/gsap.min.js';
  var ST_URL = 'https://cdn.jsdelivr.net/npm/gsap@3.12.7/dist/ScrollTrigger.min.js';
  /* No Lenis — see the "REMOVED, deliberately" note below before adding it back.
     One fewer CDN request on every page load, as a side benefit. */

  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* ── Disarm Dawn's native scroll-reveal on elements this file also owns ──
     Dawn ships its own reveal system (assets/animations.js): a
     `.scroll-trigger` class + IntersectionObserver, active whenever the
     "Animations reveal on scroll" theme setting is on (it is, here). It
     targets `.title-wrapper` / `.rich-text` / collection & product headings
     — the SAME elements HEADLINE_SEL below claims for the custom line-mask
     reveal. Both were running: two independent IntersectionObservers, two
     competing visual treatments (Dawn's opacity+class toggle vs. this
     file's GSAP yPercent transform) on one element, on every section with a
     title — which includes the homepage featured-collection section
     ("SAY WHAT NO ONE DARES") first reported as laggy, plus collection and
     product pages. That's real double work landing exactly when a new
     section scrolls into view, on top of whatever else is running.

     Dawn's observer is attached inside a DOMContentLoaded handler
     (animations.js line ~94), which fires only once every deferred script
     — including this one — has finished executing (spec-guaranteed order).
     So stripping `scroll-trigger`/`animate--*` here, synchronously, at this
     file's own top-level (before the async GSAP/CDN chain below even
     starts), is not a timing race: it is guaranteed to run first. Dawn's
     later `getElementsByClassName('scroll-trigger')` snapshot simply won't
     find these elements, and never observes them. Kept as one selector
     constant, reused by buildHeadlineReveals() below, so the two lists can
     never drift apart. */
  var HEADLINE_SEL = '.ve-gh__title, .ve-world__open-title, .ve-row2__type, .ve-split__headline, .mf-hero, .featured-collection .title-wrapper, .title-wrapper, .rich-text, .collection-hero__title-wrapper';
  document.querySelectorAll(HEADLINE_SEL).forEach(function (el) {
    el.classList.remove('scroll-trigger', 'animate--fade-in', 'animate--slide-in', 'scroll-trigger--offscreen');
  });
  /* Same conflict, one level down: Dawn tags the outer `.grid__item` (its own
     stock wrapper) with scroll-trigger/animate--slide-in on every collection
     and featured-collection grid — a DIFFERENT element than `.ve-card`
     (rendered inside it by our own card-product.liquid), so the earlier
     `.ve-card.scroll-trigger` check finds nothing even though two systems
     are still both firing for every card, nested. Scoped to grid items that
     actually contain a `.ve-card`, so blog/article grids — which have no
     custom replacement — keep Dawn's native reveal untouched. */
  document.querySelectorAll('.grid__item').forEach(function (el) {
    if (el.querySelector('.ve-card')) {
      el.classList.remove('scroll-trigger', 'animate--fade-in', 'animate--slide-in', 'scroll-trigger--offscreen');
    }
  });

  function loadScript(src, optional) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src; s.async = true;
      s.onload = resolve;
      s.onerror = optional ? resolve : reject;
      document.head.appendChild(s);
    });
  }

  /* ── Late-init guard ──
     These libraries arrive from a CDN, which on a cold connection can take
     well over a second. Anything the visitor has ALREADY scrolled to by then
     must be left exactly as it is: applying a hidden "from" state at that
     point makes on-screen content vanish and re-animate underneath them,
     which reads as the page lagging. So every reveal below is restricted to
     elements still safely below the fold when we finally boot. */
  function belowFold(el) {
    if (!el || !el.getBoundingClientRect) return false;
    var r = el.getBoundingClientRect();
    if (r.width === 0 && r.height === 0) return false; // hidden/detached
    return r.top > window.innerHeight * 0.92;
  }

  /* ── Lenis smooth-scroll: REMOVED, deliberately. Do not reinstate. ──
     Lenis replaces the browser's native scrolling with a JS-driven eased
     approximation: every wheel/trackpad input is intercepted and the page is
     moved by an easing function on each animation frame instead. That is,
     by construction, "the page does not go exactly where your fingers just
     put it" — which is precisely what a visitor reports as lag, no matter
     how well it performs in a profiler.

     It was tuned twice (lerp 0.1 → 0.22, cutting the settle time from
     ~470ms to ~180ms) and the lag was still reported both times. Tuning
     cannot fix it, because the residual delay IS the feature. Native
     scrolling is handled on the browser's compositor thread, is never
     blocked by JS, and matches the device exactly.

     This is a deliberate deviation from the design guideline (§Motion spec
     lists Lenis) — the same guideline requires "usability-safe" 60fps
     motion, and the usability requirement wins. Every reveal animation
     still works: ScrollTrigger reads native scroll perfectly well. */

  /* Nothing to set up — native scroll is the browser's default. */

  /* ── Line-mask headline reveal ──
     Each headline "line" is wrapped in an overflow-hidden clip, then slides
     up from below (yPercent 120 → 0) with a slight per-line stagger when its
     group enters view. Lines that share a parent cascade together. Overflow
     is released on completion so descenders/letter-spacing never clip. */
  function buildHeadlineReveals(gsap, ST) {
    var groupSel = HEADLINE_SEL; // kept as one constant — see top-of-file comment
    var lineSel = '.ve-gh__title > span, .ve-world__open-title, .ve-row2__title, .ve-row2__title--out, .ve-split__line, .ve-split__line--outline, .ve-split__line--sm, .mf-hero-line, .mf-hero-line-outline, .title-wrapper h2, .rich-text__heading, .collection-hero__title, .product__title';

    // Map each line element to its nearest reveal "group" so lines cascade.
    var seen = [];
    var map = new Map();
    gsap.utils.toArray(lineSel).forEach(function (line) {
      if (seen.indexOf(line) > -1) return;
      // Already on screen by the time the CDN answered — leave it alone.
      if (!belowFold(line)) return;
      seen.push(line);
      var group = line.closest(groupSel) || line.parentNode || line;
      if (!map.has(group)) map.set(group, []);
      map.get(group).push(line);
    });

    map.forEach(function (lines, group) {
      var wraps = [];
      lines.forEach(function (line) {
        var w = document.createElement('span');
        w.className = 've-lmask';
        w.style.display = 'block';
        w.style.overflow = 'hidden';
        w.style.paddingBottom = '0.02em';
        line.parentNode.insertBefore(w, line);
        w.appendChild(line);
        line.style.display = 'block';
        wraps.push(w);
      });
      gsap.set(lines, { yPercent: 120 });
      ST.create({
        trigger: group,
        start: 'top 88%',
        once: true,
        onEnter: function () {
          gsap.to(lines, {
            yPercent: 0, duration: 1.05, ease: 'power4.out', stagger: 0.1,
            onComplete: function () { wraps.forEach(function (w) { w.style.overflow = 'visible'; }); },
          });
        },
      });
    });
  }

  function init() {
    if (!window.gsap || !window.ScrollTrigger) return;
    var gsap = window.gsap;
    var ST = window.ScrollTrigger;
    gsap.registerPlugin(ST);

    var mm = gsap.matchMedia();
    mm.add('(prefers-reduced-motion: no-preference)', function () {
      /* Hero parallax removed along with the section-media parallax below.
         A `scrub` ScrollTrigger is not a one-shot reveal — it re-computes and
         writes a transform on EVERY scroll frame for as long as the element
         is in view. One-shot reveals (which is everything kept below) do
         their work once and then unbind, costing nothing while scrolling.
         With the lag reported repeatedly, continuous per-frame work during
         scroll is exactly what should go first; these were decorative drift
         on imagery most visitors never consciously registered. */

      /* Line-mask headline reveals (the signature move) */
      buildHeadlineReveals(gsap, ST);

      /* Product cards: staggered rise + fade as they enter. Only cards still
         below the fold are hidden — see belowFold() note above. */
      var cards = gsap.utils.toArray('.ve-card').filter(belowFold);
      if (cards.length) {
        gsap.set(cards, { autoAlpha: 0, y: 34 });
        ST.batch(cards, {
          start: 'top 92%',
          onEnter: function (batch) {
            gsap.to(batch, { autoAlpha: 1, y: 0, duration: 0.6, ease: 'power3.out', stagger: 0.08, overwrite: true });
          },
        });
      }

      /* Custom homepage bands: lift in (the band shell, not its headline) */
      var riseSel = ['.ve-world__open-right', '.ve-row2__photo', '.ve-split__photo', '.ve-stmt', '.ve-rules', '.ve-quote'].join(',');
      gsap.utils.toArray(riseSel).filter(belowFold).forEach(function (el) {
        gsap.from(el, {
          autoAlpha: 0, y: 40, duration: 0.75, ease: 'power3.out',
          scrollTrigger: { trigger: el, start: 'top 84%', once: true },
        });
      });

      /* (Section-media scrub parallax removed — see the note above.) */

      ST.refresh();
    });
  }

  function boot() {
    /* ScrollTrigger needs the gsap global at execution time, so it can't start
       before gsap resolves — but Lenis is independent, so the two run in
       parallel. That removes a whole round trip from what used to be three
       sequential CDN fetches (measured ~0.55s warm, over 1.2s cold), which is
       dead time where the page has no motion and content can still be
       retro-hidden underneath the visitor. */
    loadScript(GSAP_URL)
      .then(function () { return loadScript(ST_URL); })
      .then(init)
      .catch(function () { /* CDN down — site stays functional, static */ });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
